/**
 * Sorteio de atributos respeitando o orcamento da classe (starting_points).
 *
 * Espelha o comportamento server-side de _sortear_atributos
 * (backend/services/sorteio_service.py): cada atributo comeca no seu `min` e
 * os pontos do orcamento sao distribuidos um a um entre os atributos que
 * ainda nao atingiram o `max`. A soma final e sempre a mesma para o mesmo
 * orcamento — so o perfil muda.
 *
 * Fase 1 de docs/PLANO_FEEDBACK_ADRIAN.md: corrige o bug do "Aleatorizar"
 * dos dialogos de personagem, que sorteava cada atributo livremente em
 * [min, max] ignorando starting_points.
 */

export interface SorteioAttribute {
  id: string
  min: number
  max: number
}

export function sortearAtributos(
  attributes: SorteioAttribute[],
  startingPoints: number,
  rng: () => number = Math.random,
): Record<string, number> {
  const values: Record<string, number> = {}
  const capacity: Record<string, number> = {}

  for (const attr of attributes) {
    values[attr.id] = attr.min
    capacity[attr.id] = Math.max(0, attr.max - attr.min)
  }

  let available = attributes.filter(a => capacity[a.id] > 0).map(a => a.id)
  let pool = Math.max(0, Math.floor(startingPoints || 0))

  while (pool > 0 && available.length > 0) {
    const pick = available[Math.floor(rng() * available.length)]
    values[pick] += 1
    capacity[pick] -= 1
    if (capacity[pick] === 0) {
      available = available.filter(id => id !== pick)
    }
    pool -= 1
  }

  return values
}

/**
 * Pontos gastos acima dos minimos — a mesma contabilidade do sorteio.
 * (Sera reusada na fase 2: contador "usados X / disponiveis Y" na criacao.)
 */
export function pontosUsados(
  attributes: SorteioAttribute[],
  values: Record<string, number>,
): number {
  return attributes.reduce(
    (sum, attr) => sum + Math.max(0, (values[attr.id] ?? attr.min) - attr.min),
    0,
  )
}

/**
 * Fase 2 — teto na criacao manual: limita um novo valor de atributo ao
 * orcamento restante da classe, alem do min/max do proprio atributo.
 *
 * Com `budget: null` (mestre — livre) so aplica min/max. Quando os valores
 * atuais ja excedem o orcamento (config inconsistente, defaults gordos),
 * o teto do atributo fica no valor atual: da para REDUZIR, nunca aumentar.
 */
export function clampComOrcamento(
  attributes: SorteioAttribute[],
  values: Record<string, number>,
  attrId: string,
  requested: number,
  budget: number | null,
): number {
  const attr = attributes.find(a => a.id === attrId)
  if (!attr) return requested
  let value = Math.min(Math.max(requested, attr.min), attr.max)
  if (budget !== null) {
    const current = values[attrId] ?? attr.min
    const usadosOutros = pontosUsados(attributes, values) - Math.max(0, current - attr.min)
    const maxPeloOrcamento = attr.min + Math.max(0, budget - usadosOutros)
    value = Math.min(value, Math.max(maxPeloOrcamento, Math.min(current, attr.max)))
  }
  return value
}
